// React Imports
import { useEffect, useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// Module Imports
import Axios from 'axios';

// Components import
import { AuthContext, useTranslation, useAPI } from 'components/lib';

// MUI imports
import { Box, Typography, Paper, CircularProgress, Alert, Container } from '@mui/material';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import TaskAltRoundedIcon from '@mui/icons-material/TaskAltRounded';

export const PaymentSuccess = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const context = useContext(AuthContext);

	// Getting the page to redirect to
	const searchParams = new URLSearchParams(window.location.search);
	const redirect = searchParams.get('product');

	// State variables
	const [countdown, setCountdown] = useState(5);
	const [invoices, setInvoices] = useState(null);
	const [isDataLoaded, setIsDataLoaded] = useState(false);
	const [showManualRedirect, setShowManualRedirect] = useState(false);
	const [accountData, setAccountData] = useState(null);
	const [accountLoading, setAccountLoading] = useState(false);

	// Load invoice data if needed
	useEffect(() => {
		const fetchData = async () => {
			try {
				//If redirect is account fetch account data
				if (redirect === 'account') {
					setAccountLoading(true);
					const res = await Axios.get(context?.user?.plan === 'hybrid' ? '/api/user' : '/api/account');
					setAccountData(res?.data?.data);
					setAccountLoading(false);
					return;
				}

				//If redirect is not account or interview practice then fetch invoice details
				if (redirect !== 'account' && redirect !== 'interview_practice') {
					const res = await Axios.get('/api/account/invoice');
					setInvoices(res?.data);
				}
			} catch (error) {
				console.error(`Error Fetching data:`, error?.message);
			}
		};

		fetchData();
	}, [redirect]);

	// Update context and mark data as loaded when account data is available
	useEffect(() => {
		if (redirect === 'account' && accountData && !accountLoading) {
			context.update({
				plan: accountData?.plan,
				subscription: accountData?.razorpay_customer_id !== null && 'active',
				permission: accountData?.permission,
				token: accountData?.token,
			});
			setIsDataLoaded(true);
		} else if (redirect !== 'account') {
			// For non-account redirects, we don't need to wait for account data
			setIsDataLoaded(true);
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [accountData, accountLoading, redirect]);

	// Handle redirection and countdown
	useEffect(() => {
		if (!isDataLoaded) return;

		const redirectTimeout = setTimeout(() => {
			if (redirect === 'account') {
				navigate(`/navigate`);
			} else if (redirect !== null) {
				if (redirect === 'lab') {
					navigate('/labs');
				} else if (redirect === 'project') {
					navigate('/projects')
				} else if (redirect === 'stream') {
					navigate('/streams');
				} else if (redirect === 'cert') {
					navigate('/lpaths');
				} else if (redirect === 'users') {
					navigate('/account/users');
				} else if (redirect === 'interview_practice') {
					navigate('/interview/topics');
				} else if (redirect === 'internship') {
					navigate('/internships')
				}
			} else {
				navigate(`/navigate`);
			}
		}, countdown * 1000);

		const countdownInterval = setInterval(() => {
			setCountdown((prevCount) => {
				if (prevCount <= 1) {
					clearInterval(countdownInterval);
					return 0;
				}
				return prevCount - 1;
			});
		}, 1000);

		return () => {
			clearTimeout(redirectTimeout);
			clearInterval(countdownInterval);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isDataLoaded, navigate, redirect]);

	// Show manual redirect option after timeout
	useEffect(() => {
		const manualRedirectTimeout = setTimeout(() => {
			setShowManualRedirect(true);
		}, 12000);

		return () => clearTimeout(manualRedirectTimeout);
	}, []);

	useEffect(() => {
		if (invoices?.data?.[0]) {
			const invoice = invoices?.data[0];
			window.dataLayer = window.dataLayer || [];
			window.dataLayer.push({
				event: 'purchaseSuccessful',
				data: {
					timestamp: invoice?.date,
					invoiceId: invoice?.number,
					amount: invoice?.total,
					invoiceUrl: invoice?.invoice_url,
					product: redirect || null,
					customerDetails: {
						name: invoice?.customer_details.name,
						email: invoice?.customer_details.email || null,
					},
					lineItems:
						invoice?.line_items?.map((item) => ({
							itemName: item?.name,
							price: item?.amount / 100 || 0,
						})) || [],
				},
			});
		}
	}, [invoices, redirect]);

	// Get proper destination text based on redirect param
	const getDestinationText = () => {
		if (redirect === 'account') return t('Payment.PaymentSuccessful.destination.dashboard');
		if (redirect === 'lab') return t('Payment.PaymentSuccessful.destination.labs');
		if (redirect === 'project') return t('Payment.PaymentSuccessful.destination.projects');
		if (redirect === 'stream') return t('Payment.PaymentSuccessful.destination.streams');
		if (redirect === 'cert') return t('Payment.PaymentSuccessful.destination.learningPaths');
		if (redirect === 'users') return t('Payment.PaymentSuccessful.destination.users');
		if (redirect === 'interview_practice') return t('Payment.PaymentSuccessful.destination.interviewPractice');
		return t('Payment.PaymentSuccessful.destination.dashboard');
	};

	return (
		<Container
			maxWidth="sm"
			sx={{
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'center',
				justifyContent: 'center',
				minHeight: '100vh',
				py: 4,
			}}
		>
			<Paper
				elevation={3}
				sx={{
					width: '100%',
					borderRadius: 2,
					overflow: 'hidden',
				}}
			>
				<Box
					sx={{
						bgcolor: '#22c55e',
						p: 4,
						display: 'flex',
						justifyContent: 'center',
					}}
				>
					<TaskAltRoundedIcon sx={{ color: 'white', fontSize: 80 }} />
				</Box>

				<Box sx={{ p: 4 }}>
					<Typography variant="h4" component="h1" align="center" gutterBottom fontWeight="bold">
						{t('Payment.PaymentSuccessful.success')}
					</Typography>

					{accountLoading ? (
						<Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
							<CircularProgress size={24} sx={{ mr: 1 }} />
							<Typography>{t('Payment.PaymentSuccessful.redirect.loading')}</Typography>
						</Box>
					) : (
						<Typography variant="body1" align="center" color="text.secondary" sx={{ mb: 3 }}>
							{redirect === 'account'
								? t('Payment.PaymentSuccessful.redirect.account')
								: t('Payment.PaymentSuccessful.redirect.default')}
						</Typography>
					)}

					{invoices?.data && (
						<Typography variant="body2" align="center" sx={{ mb: 2 }}>
							{t('Payment.PaymentSuccessful.invoice', { invoiceNumber: invoices?.data[0]?.number })}
						</Typography>
					)}

					{isDataLoaded && (
						<Box
							sx={{
								display: 'flex',
								justifyContent: 'center',
								mb: 3,
							}}
						>
							<Box
								sx={{
									bgcolor: '#dcfce7',
									color: 'success.dark',
									px: 2,
									py: 1,
									borderRadius: 10,
									fontSize: '0.9rem',
									fontWeight: 500,
								}}
							>
								{t('Payment.PaymentSuccessful.redirect.redirecting', {
									destination: getDestinationText(),
									countdown,
								})}
							</Box>
						</Box>
					)}

					<Alert
						severity="warning"
						icon={<WarningAmberOutlinedIcon />}
						sx={{
							bgcolor: '#fffbeb',
							mb: 2,
							borderRadius: '8px',
						}}
					>
						{t('Payment.PaymentSuccessful.warning')}
					</Alert>

					{showManualRedirect && (
						<Box
							sx={{
								display: 'flex',
								justifyContent: 'center',
								mt: 2,
							}}
						>
							<Typography
								variant="body2"
								sx={{
									color: 'primary.main',
									cursor: 'pointer',
									textDecoration: 'underline',
									fontWeight: 500,
								}}
								onClick={() => {
									if (redirect === 'account') {
										navigate('/navigate');
									} else if (redirect === 'lab') {
										navigate('/labs');
									} else if (redirect === 'streams') {
										navigate('/streams');
									} else if (redirect === 'cert') {
										navigate('/lpaths');
									} else if (redirect === 'users') {
										navigate('/account/users');
									} else {
										navigate('/navigate');
									}
								}}
							>
								{t('Payment.PaymentSuccessful.tooLong', {
									destination: getDestinationText().toLowerCase(),
								})}
							</Typography>
						</Box>
					)}
				</Box>
			</Paper>
		</Container>
	);
};

